"use client";
import React from "react";
import { useCustomImageContext } from "../../contexts/CustomImageContext";
import { HiXCircle } from "react-icons/hi2";
import Image from "next/image";
import { ColorType } from "@/utils/APIRouteTypes";
import { useGeneratedImages } from "@/contexts/GeneratedImagesProvider";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { cn } from "@/lib/utils";
import { getImageInitialProps } from "@/utils/helper";
import { ImageType } from "@/types/contextTypes";
import { Properties } from "@/utils/properties";

export default function SelectedImageViewer() {
  const [imgContext, _] = useCustomImageContext();
  const [generatedImages, setGeneratedImages] = useGeneratedImages();
  const frontLen = imgContext.frontImages.length;
  const backLen = imgContext.backImages.length;
  if (frontLen + backLen === 0) return <></>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 px-0 md:px-2">
      {frontLen > 0 && (
        <div>
          <h4 className="text-sm md:text-base font-semibold dark:text-white">Front Images</h4>
          <p className="text-xs md:text-sm text-slate-500">
            Here are the images you have selected
          </p>
          <div className="flex">
            {imgContext.frontImages.map((img, index) => (
              <ImageItem
                src={img.src}
                key={index}
                contextKey="frontImages"
                colorType={generatedImages.find(generatedImg => generatedImg.src === img.src)?.color || ColorType.DARK}
              />
            ))}
          </div>
        </div>
      )}

      {backLen > 0 && (
        <div>
          <h4 className="text-sm md:text-base font-semibold dark:text-white">Back Images</h4>
          <p className="text-xs md:text-sm text-slate-500">
            Here are the images you have selected
          </p>
          <div className="grid grid-cols-6">
            {imgContext.backImages.map((img, index) => (
              <ImageItem
                src={img.src}
                key={index}
                contextKey="backImages"
                colorType={generatedImages.find(generatedImg => generatedImg.src === img.src)?.color || ColorType.DARK}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type ImageItemProps = {
  src: string;
  colorType: ColorType
  contextKey: "backImages" | "frontImages";
};

const ImageItem = (props: ImageItemProps) => {
  const [imgContext, setImgContext] = useCustomImageContext();

  const handleImageRemove = () => {
    setImgContext((prev) => {
      return {
        ...prev,
        [props.contextKey]: prev[props.contextKey].filter(
          (img) => img.src !== props.src,
        ),
      };
    });
  };

  const k = props.contextKey === "frontImages" ? "backImages" : "frontImages";
  const copyToOtherSide = () => {

    //check if image already exists
    if (imgContext[k].find((img) => img.src === props.src)) {
      return;
    }

    setImgContext((prev) => {
      const newValue: ImageType = {
        src: props.src,
        scale: Properties.defaultImgScale,
        x: 0,
        y: 0,
      }
      return {
        ...prev,
        [k]: [
          ...prev[k],
          newValue
        ],
      };
    });
  }

  return (
    <div className="flex flex-col items-center justify-center -me-3 mt-3">
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className={cn('aspect-square overflow-hidden shadow-lg rounded-full ring-2 ring-offset-2 p-1 z-20 hover:scale-110 transition-all cursor-pointer duration-200', props.colorType === ColorType.DARK ? "bg-slate-900 ring-ffmain-600" : "bg-ffaccent-100 ring-ffaccent-500 border")}>
            <Image
              src={props.src}
              alt=""
              className={cn("object-cover size-16 object-center scale-125 z-0", props.colorType === ColorType.DARK ? "mix-blend-lighten" : "mix-blend-multiply")}
              draggable={false}
              width={100}
              height={100}
            />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          <ContextMenuItem onClick={handleImageRemove} >Remove</ContextMenuItem>
          <ContextMenuItem onClick={copyToOtherSide} disabled={imgContext[k].findIndex((img) => img.src === props.src) !== -1} >Copy To Other Side</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>


      <button className="text-red-700 mt-1" onClick={handleImageRemove}>
        <HiXCircle className="h-7 w-7" />
      </button>
    </div>
  );
};

